import { ArrowUpRight, Building2, CreditCard, ShieldAlert, UserRound } from 'lucide-react';
import { Link } from 'react-router-dom';

import type { SuperAdminDashboardSnapshot } from '@/features/dashboard/dashboardTypes';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { PageHeader } from '@/components/page-header';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

type SuperAdminDashboardProps = {
  fullName: string;
  snapshot: SuperAdminDashboardSnapshot;
  currentOrgContext: {
    organizationName?: string;
    roleLabel: string;
    organizationDetailsHref?: string;
  } | null;
};

function KpiCard({
  title,
  value,
  description,
  icon: Icon,
}: {
  title: string;
  value: number;
  description: string;
  icon: typeof Building2;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="text-muted-foreground h-4 w-4" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        <p className="text-muted-foreground text-xs">{description}</p>
      </CardContent>
    </Card>
  );
}

function formatDate(value?: number) {
  if (!value) return 'N/A';
  const ms = value < 1_000_000_000_000 ? value * 1000 : value;
  return new Date(ms).toLocaleString();
}

function DistributionCard({
  title,
  description,
  items,
  total,
}: {
  title: string;
  description: string;
  items: Array<{ key: string; label: string; count: number }>;
  total: number;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {items.map((item) => {
          const pct = total > 0 ? Math.round((item.count / total) * 100) : 0;
          return (
            <div key={item.key} className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <span>{item.label}</span>
                <span className="text-muted-foreground">
                  {item.count} ({pct}%)
                </span>
              </div>
              <div className="bg-muted h-2 w-full overflow-hidden rounded-full">
                <div className="bg-primary h-full" style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

export function SuperAdminDashboard({ fullName, snapshot, currentOrgContext }: SuperAdminDashboardProps) {
  const subscriptionsTotal = snapshot.subscriptionStatusBreakdown.reduce((acc, item) => acc + item.count, 0);
  return (
    <div className="space-y-6">
      <PageHeader
        title={`Super Admin Console · ${fullName}`}
        description={
          <div className="space-y-1">
            <p>Cross-organization operational dashboard (placeholder snapshot + loaded store data).</p>
            {currentOrgContext ? (
              <div className="flex flex-wrap items-center gap-2 text-sm">
                {currentOrgContext.organizationName ? (
                  <span className="text-foreground font-medium">{currentOrgContext.organizationName}</span>
                ) : null}
                {currentOrgContext.organizationName ? <span className="text-muted-foreground">•</span> : null}
                <span className="text-muted-foreground capitalize">{currentOrgContext.roleLabel}</span>
                {currentOrgContext.organizationDetailsHref ? (
                  <Button asChild variant="ghost" size="sm" className="h-6 px-1">
                    <Link to={currentOrgContext.organizationDetailsHref} aria-label="Open organization details">
                      <ArrowUpRight className="h-3.5 w-3.5" />
                    </Link>
                  </Button>
                ) : null}
              </div>
            ) : null}
          </div>
        }
        right={
          <Button asChild>
            <Link to="/admin/organizations">Manage Organizations</Link>
          </Button>
        }
      />

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        <KpiCard
          title="Organizations"
          value={snapshot.organizationsCount}
          description="All organizations in cache."
          icon={Building2}
        />
        <KpiCard
          title="Properties"
          value={snapshot.propertiesCount}
          description="All projects/properties currently loaded."
          icon={Building2}
        />
        <KpiCard
          title="Home Owners"
          value={snapshot.homeOwnersCount}
          description="Unique property owners in loaded data."
          icon={UserRound}
        />
        <KpiCard
          title="Active Subscriptions"
          value={snapshot.activeSubscriptionsCount}
          description="Subscription status = active."
          icon={CreditCard}
        />
        <KpiCard
          title="Past Due Subscriptions"
          value={snapshot.pastDueSubscriptionsCount}
          description="Requires billing follow-up."
          icon={ShieldAlert}
        />
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <DistributionCard
          title="Subscription Tier Mix"
          description="Current distribution by plan tier."
          total={subscriptionsTotal}
          items={snapshot.subscriptionTierBreakdown.map((item) => ({
            key: item.tier,
            label: item.tier.toUpperCase(),
            count: item.count,
          }))}
        />
        <DistributionCard
          title="Subscription Status Mix"
          description="Current distribution by lifecycle status."
          total={subscriptionsTotal}
          items={snapshot.subscriptionStatusBreakdown.map((item) => ({
            key: item.status,
            label: item.status.replace('_', ' '),
            count: item.count,
          }))}
        />
      </div>

      <div className="grid gap-4 xl:grid-cols-3">
        <Card className="xl:col-span-2">
          <CardHeader>
            <CardTitle>Recent Projects</CardTitle>
            <CardDescription>Latest updated projects currently loaded in state.</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Project ID</TableHead>
                  <TableHead>Organization</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Updated</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {snapshot.recentProjects.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-muted-foreground text-sm">
                      No projects loaded yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  snapshot.recentProjects.map((project) => (
                    <TableRow key={project.id}>
                      <TableCell>
                        <Button asChild variant="link" className="h-auto p-0">
                          <Link to={`/properties/${encodeURIComponent(project.id)}/edit`}>{project.id}</Link>
                        </Button>
                      </TableCell>
                      <TableCell>{project.orgID ?? 'N/A'}</TableCell>
                      <TableCell>
                        <Badge variant={project.status === 'owned' ? 'default' : 'secondary'}>{project.status}</Badge>
                      </TableCell>
                      <TableCell>{formatDate(project.updatedAt)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Operational Alerts</CardTitle>
            <CardDescription>Actionable checks for platform operations.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {snapshot.operationalAlerts.map((alert) => (
              <div key={alert.id} className="flex items-start justify-between gap-2 rounded-md border p-3">
                <div className="space-y-1">
                  <p className="text-sm">{alert.label}</p>
                  {alert.href ? (
                    <Button asChild variant="link" size="sm" className="h-auto p-0">
                      <Link to={alert.href}>Open</Link>
                    </Button>
                  ) : null}
                </div>
                <Badge variant={alert.tone === 'critical' ? 'destructive' : 'secondary'}>{alert.tone}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
